import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdvancedMarker, AdvancedMarkerAnchorPoint, APIProvider, ControlPosition, InfoWindow, Map, MapControl, Marker, Pin } from '@vis.gl/react-google-maps';
import { PlaceAutocompleteClassic } from './PlaceAutoCompleteClassic';
import MapHandler from './MapHandler';
import MarkerWithInfoWindow from './MarkerWithInfoWindow';
import CompanyInfoWindow from './CompanyInfoWindow';
import useDistanceMatrix from './hooks/useDistanceMatrix';
import { getAddress, getDistance, getDuration, getLatLng, getName, getOtherCompanyInfo } from './utils';
import { ArrowBack } from '@material-ui/icons';
import { IconButton } from '@material-ui/core';
import MapBoundary from './MapBoundary';
import CopyToClipboard from '../utilComponents/CopyToClipboard';

const API_KEY = "AIzaSyCvOQLQzL6EoVq6Kh8z6V3CE0HnoXKspfk"

const AddressMap = ({ searchedPlace, companies, handlePlaceSelect, boundaryInfo = null, setLoading, handleClose }) => {


    const [hoverId, setHoverId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companyInfo, setCompanyInfo] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [infoWindowShown, setInfoWindowShown] = useState(false);

    const [roadDuration, setRoadDuration] = useState(null);
    const [roadDistance, setRoadDistance] = useState(null);

    const [markerRefs, setMarkerRefs] = useState({}); // triggers rerender
    const markerRefsRef = useRef(markerRefs); // keeps the latestest marker ref

    // Keep the latest markerRefs inside the ref
    useEffect(() => {
        markerRefsRef.current = markerRefs;
    }, [markerRefs]);

    // Auto-select the closest company when markers are updated
    // useEffect(() => {
    //     if (companies.length > 0) {
    //         const interval = setInterval(() => {
    //             if (markerRefsRef.current && Object.keys(markerRefsRef.current).length > 0) {
    //                 const firstCompany = companies[0];
    //                 const firstMarker = markerRefsRef.current[firstCompany.defaultInstanceAttribute.id];
    //                 if (firstMarker) {
    //                     onMarkerClick(firstCompany.defaultInstanceAttribute.id, searchedPlace, firstCompany, firstMarker);
    //                     clearInterval(interval);
    //                 }
    //             }
    //         }, 100);
    //         return () => clearInterval(interval);
    //     }
    // }, [companies, searchedPlace]);

    // map marker refs with companyId
    const updateMarkers = (companyId, marker) => {
        setMarkerRefs((prevMarkers) => ({
            ...prevMarkers,
            [companyId]: marker
        }));
    };


    const { distanceMatrix: data, isLoading, error, fetchDistanceMatrix } = useDistanceMatrix();

    useEffect(() => {
        const fetchPlaceDetails = async () => {
            if (searchedPlace) {
                setSelectedId(null)
                setSelectedCompany(null)
                setSelectedMarker(null)
                setInfoWindowShown(false)
                setMarkerRefs({})
                await handlePlaceSelect(searchedPlace);
            }
        };
        fetchPlaceDetails();
    }, [searchedPlace]);

    const onMouseEnter = useCallback((id) => setHoverId(id), []);
    const onMouseLeave = useCallback(() => setHoverId(null), []);
    const onMarkerClick = useCallback(async (id, searched, selected, marker) => {
        if (marker) {
            setSelectedMarker(marker);
            setSelectedId(id);
            setSelectedCompany(selected);
            setCompanyInfo(getOtherCompanyInfo(selected));

            const origin = `${searched.geometry.location.lat()},${searched.geometry.location.lng()}`;

            const location = getLatLng(selected)
            if (location) {
                const destination = `${location.lat()},${location.lng()}`;
                //setLoading(true)
                const distanceMatrix = await fetchDistanceMatrix(origin, destination, API_KEY);
                console.log("distanceMatrix", distanceMatrix);
                setRoadDistance(getDistance(distanceMatrix));
                setRoadDuration(getDuration(distanceMatrix));
                //setLoading(false)
            }
        }

        if (id !== selectedId) {
            setInfoWindowShown(true);
        } else {
            setInfoWindowShown(false);
            setSelectedId(null);
            setSelectedCompany(null);
        }
    }, [selectedId]);

    const handleInfowindowCloseClick = useCallback(() => {
        setInfoWindowShown(false)
        //setSelectedId(null);
        //setSelectedCompany(null);
    }, []);

    return (
        <div className='address-map-page-container'>
            <div id='address-map'>
                <APIProvider apiKey={API_KEY}>
                    <div style={{ height: '100%', width: '100%', flex: 2 }}>
                        <Map
                            defaultZoom={8}
                            defaultCenter={{ lat: 53.54, lng: 10 }}
                            // disableDefaultUI={true}
                            streetView={false}
                            streetViewControl={false}
                            mapTypeControl={false}
                            mapId={"6c213c088b16f9bc"}
                        >
                            <>
                                {
                                    searchedPlace?.geometry?.location && (
                                        // Replace simple marker with custom Advance Marker
                                        <AdvancedMarker
                                            position={searchedPlace?.geometry?.location}
                                            style={{ transform: 'scale(1.3)' }}
                                            zIndex={selectedId != null ? 99999 : -1}
                                        >
                                            <Pin
                                                background={"#27ae60"}
                                                borderColor={"#1e8449"}
                                                glyphColor={"#145a32"}
                                            />
                                        </AdvancedMarker>
                                    )
                                }

                                {companies.map((company, index) => {

                                    const defaultData = company.defaultInstanceAttribute;
                                    const location = getLatLng(company)

                                    if (!location) return null;

                                    return (
                                        <MarkerWithInfoWindow
                                            key={defaultData.id}
                                            onMarkerClick={(marker) => onMarkerClick(defaultData.id, searchedPlace, company, marker)}
                                            onMouseEnter={() => onMouseEnter(defaultData.id)}
                                            onMouseLeave={onMouseLeave}
                                            companyId={defaultData.id}
                                            updateMarkers={updateMarkers}
                                            zIndex={defaultData.id == selectedId ? 99999 : index + 1}
                                            className="custom-marker"
                                            style={{
                                                transform: `scale(${[hoverId, selectedId].includes(defaultData.id) ? 1.3 : 1})`,
                                                transformOrigin: AdvancedMarkerAnchorPoint['BOTTOM'].join(' ')
                                            }}
                                            position={location}>
                                            <Pin
                                                background={selectedId === defaultData.id ? '#ffcc66' : null}
                                                borderColor={selectedId === defaultData.id ? '#e6a000' : null}
                                                glyphColor={selectedId === defaultData.id ? '#cc8400' : null}
                                            />
                                        </MarkerWithInfoWindow>
                                    )

                                })}
                            </>

                            {infoWindowShown && selectedMarker && selectedCompany && (
                                <CompanyInfoWindow
                                    anchor={selectedMarker}
                                    company={selectedCompany}
                                    pixelOffset={[0, -2]}
                                    onCloseClick={handleInfowindowCloseClick}
                                    roadDistance={roadDistance}
                                    roadDuration={roadDuration}
                                />
                            )}
                        </Map>
                    </div>


                    {/* Search Input */}
                    {/* <MapControl position={ControlPosition.TOP_LEFT} className="map-control">
                        <div className="autocomplete-control autocomplete-control-inline">
                            <PlaceAutocompleteClassic onPlaceSelect={handlePlaceSelect} />
                        </div>
                    </MapControl> */}

                    {/* Handle map when place is selected */}
                    <MapHandler place={searchedPlace} locations={companies} />

                    <MapBoundary place={searchedPlace} boundaryInfo={boundaryInfo} locations={companies} />
                </APIProvider>
            </div>
            <div id='address-map-sidebar'>
                <div className='address-map-sidebar-header'>
                    <IconButton onClick={handleClose}><ArrowBack /></IconButton>
                    <h2 style={{ flex: 2 }}>Link Search Address</h2>
                </div>

                <div className='address-map-section'>
                    <h3>Searched Location</h3>
                    <CopyToClipboard>{searchedPlace.formatted_address}</CopyToClipboard>
                    <CopyToClipboard>Coordinates: {searchedPlace.geometry.location.lat().toFixed(4)}, {searchedPlace.geometry.location.lng().toFixed(4)}</CopyToClipboard>
                </div>
                {
                    selectedCompany && (
                        <div className='address-map-section address-map-selected-company'>
                            <h3>Selected Company</h3>
                            <div className="company-info">
                                <div className="icon"></div>
                                <div>
                                    <CopyToClipboard>{getAddress(selectedCompany)}</CopyToClipboard>
                                    {selectedCompany.miles !== null && (
                                        <CopyToClipboard
                                            className="distance-time"
                                            text={`Distance: ${selectedCompany.miles} miles | Time by Car: ${roadDuration}`}
                                        >
                                            {/* Distance: <strong>{selectedCompany.miles} miles</strong> | Road Miles: <strong>{roadDistance}</strong> | Time by Car: <strong>{roadDuration}</strong> */}
                                            Distance: <strong>{selectedCompany.miles} miles</strong> | Time by Car: <strong>{roadDuration}</strong>
                                        </CopyToClipboard>
                                    )}
                                </div>
                            </div>
                            {
                                companyInfo && companyInfo.length > 0 && (
                                    <>
                                        <hr />
                                        <div className="company-info-items-container">

                                            {companyInfo.map((info, index) => (
                                                <CopyToClipboard text={info.content} className="distance-time"><strong>{info.attribute}</strong>: {info.content}</CopyToClipboard>
                                            ))}

                                        </div>
                                    </>
                                )
                            }
                        </div>
                    )
                }

                {searchedPlace && companies.length > 0 && (
                    <div className='address-map-section'>
                        <h3>Nearby Companies</h3>
                        <div className="list-item-container" style={{ maxHeight: '70%', overflowY: 'auto' }}>
                            {companies.map((company, index) => (
                                <div
                                    key={company.defaultInstanceAttribute.id} // Add unique key prop here
                                    className="list-item"
                                    onClick={() => {
                                        const selectedMarker = markerRefsRef.current[company.defaultInstanceAttribute.id];
                                        onMarkerClick(company.defaultInstanceAttribute.id, searchedPlace, company, selectedMarker)
                                    }}>
                                    <div className="company-info">
                                        <div className={company.defaultInstanceAttribute.id == selectedId ? "icon-active" : "icon"}></div>
                                        <span>{getAddress(company)}</span>
                                    </div>
                                    {company.miles !== null && (<span className="distance-time">{company.miles} miles</span>)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddressMap;